import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Fab,
  Paper,
  Box,
  Stack,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Badge,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import apiHandler from '../handlers/ApiHandler';

// Hand-rolled live chat widget. First-party only (our own API, no third party).
// - Floating bubble bottom-right; toggles a small panel above it.
// - Anonymous persistent visitorId in localStorage (cookieless).
// - Polls the thread every ~5s while open; stops when closed.
// - Everything wrapped in try/catch — must never throw or block the page.

const STORAGE_ID_KEY = 'ep_chat_id';
const POLL_MS = 5000;

// Read (or lazily create) a stable anonymous visitor id.
const getVisitorId = () => {
  try {
    let id = window.localStorage.getItem(STORAGE_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `ep-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(STORAGE_ID_KEY, id);
    }
    return id;
  } catch (e) {
    // localStorage blocked (private mode, etc.) — fall back to a session id.
    return `ep-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
};

const messageKey = (m, i) =>
  `${m && m.createdAt ? m.createdAt : 'm'}-${i}-${m && m.from ? m.from : ''}`;

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [name, setName] = useState('');
  const [nameCollected, setNameCollected] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);

  const visitorIdRef = useRef(null);
  const listEndRef = useRef(null);
  // Count of agent messages last time the panel was open, to detect new replies.
  const seenAgentCountRef = useRef(0);

  if (!visitorIdRef.current) {
    visitorIdRef.current = getVisitorId();
  }

  const scrollToBottom = useCallback(() => {
    try {
      if (listEndRef.current && listEndRef.current.scrollIntoView) {
        listEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    } catch (e) {
      /* ignore */
    }
  }, []);

  // Fetch the full thread. `markSeen` clears the unread state (panel is open).
  const fetchThread = useCallback(
    async (markSeen) => {
      try {
        const res = await apiHandler.get(
          `/chat/thread?visitorId=${encodeURIComponent(visitorIdRef.current)}`
        );
        const next =
          res && Array.isArray(res.messages) ? res.messages : [];
        setMessages(next);

        const agentCount = next.filter(
          (m) => m && m.from === 'agent'
        ).length;

        if (markSeen) {
          seenAgentCountRef.current = agentCount;
          setHasUnread(false);
        } else if (agentCount > seenAgentCountRef.current) {
          setHasUnread(true);
        }
      } catch (e) {
        /* tolerate silently — never break the site */
      }
    },
    []
  );

  // Load existing thread when the panel first opens, then poll while open.
  useEffect(() => {
    if (!open) return undefined;
    fetchThread(true);
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchThread(true);
    }, POLL_MS);
    return () => clearInterval(id);
  }, [open, fetchThread]);

  // Poll in the background (slower) while closed so the unread dot can appear.
  useEffect(() => {
    if (open) return undefined;
    // Prime the "seen" baseline once, quietly.
    let cancelled = false;
    (async () => {
      try {
        const res = await apiHandler.get(
          `/chat/thread?visitorId=${encodeURIComponent(visitorIdRef.current)}`
        );
        if (cancelled) return;
        const next = res && Array.isArray(res.messages) ? res.messages : [];
        const agentCount = next.filter((m) => m && m.from === 'agent').length;
        if (seenAgentCountRef.current === 0 && agentCount > 0 && messages.length === 0) {
          // First-ever load with prior agent messages: treat as already seen.
          seenAgentCountRef.current = agentCount;
        } else if (agentCount > seenAgentCountRef.current) {
          setHasUnread(true);
        }
      } catch (e) {
        /* ignore */
      }
    })();

    const id = setInterval(async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const res = await apiHandler.get(
          `/chat/thread?visitorId=${encodeURIComponent(visitorIdRef.current)}`
        );
        if (cancelled) return;
        const next = res && Array.isArray(res.messages) ? res.messages : [];
        const agentCount = next.filter((m) => m && m.from === 'agent').length;
        if (agentCount > seenAgentCountRef.current) setHasUnread(true);
      } catch (e) {
        /* ignore */
      }
    }, POLL_MS * 3);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) scrollToBottom();
  }, [messages, open, scrollToBottom]);

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) setHasUnread(false);
      return next;
    });
  };

  const handleSend = useCallback(async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);

    // Optimistically append the visitor's message.
    const optimistic = {
      from: 'visitor',
      body,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft('');
    if (name.trim()) setNameCollected(true);

    try {
      await apiHandler.post('/chat/message', {
        visitorId: visitorIdRef.current,
        name: name.trim() || undefined,
        body,
      });
      // Refetch the canonical thread (dedupes the optimistic entry).
      await fetchThread(true);
    } catch (e) {
      /* tolerate silently — optimistic message stays visible */
    } finally {
      setSending(false);
    }
  }, [draft, sending, name, fetchThread]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const showNameField = !nameCollected && messages.length === 0;

  return (
    <Box
      sx={{
        position: 'fixed',
        right: 24,
        bottom: 24,
        zIndex: (t) => t.zIndex.snackbar + 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      {open && (
        <Paper
          elevation={6}
          sx={{
            width: 340,
            maxWidth: 'calc(100vw - 48px)',
            height: 460,
            maxHeight: 'calc(100vh - 120px)',
            mb: 1.5,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.25,
              backgroundColor: 'text.primary',
              color: 'background.paper',
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Chat with Ear Physics
            </Typography>
            <IconButton
              size="small"
              aria-label="close chat"
              onClick={() => setOpen(false)}
              sx={{
                color: 'background.paper',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Message list */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              px: 1.5,
              py: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {messages.length === 0 && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', textAlign: 'center', mt: 2 }}
              >
                Hi! Send us a message and we'll get back to you here.
              </Typography>
            )}
            {messages.map((m, i) => {
              const isVisitor = m && m.from === 'visitor';
              return (
                <Box
                  key={messageKey(m, i)}
                  sx={{
                    display: 'flex',
                    justifyContent: isVisitor ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '80%',
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 2,
                      border: 1,
                      borderColor: isVisitor ? 'text.primary' : 'divider',
                      backgroundColor: isVisitor
                        ? 'text.primary'
                        : 'action.hover',
                      color: isVisitor ? 'background.paper' : 'text.primary',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {m && m.body}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
            <div ref={listEndRef} />
          </Box>

          {/* Footer */}
          <Box
            sx={{
              flexShrink: 0,
              borderTop: 1,
              borderColor: 'divider',
              p: 1.5,
            }}
          >
            <Stack spacing={1}>
              {showNameField && (
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  inputProps={{ 'aria-label': 'your name' }}
                />
              )}
              <TextField
                size="small"
                fullWidth
                multiline
                maxRows={3}
                placeholder="Type a message…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                inputProps={{ 'aria-label': 'chat message' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="send message"
                        onClick={handleSend}
                        disabled={sending || !draft.trim()}
                        sx={{
                          color: 'text.primary',
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                      >
                        <SendIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Box>
        </Paper>
      )}

      {/* Bubble */}
      <Badge
        color="error"
        variant="dot"
        overlap="circular"
        invisible={open || !hasUnread}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Fab
          aria-label={open ? 'close chat' : 'open chat'}
          onClick={toggleOpen}
          sx={{
            backgroundColor: 'text.primary',
            color: 'background.paper',
            boxShadow: 3,
            '&:hover': { backgroundColor: 'text.secondary' },
          }}
        >
          {open ? <CloseIcon /> : <ChatBubbleOutlineIcon />}
        </Fab>
      </Badge>
    </Box>
  );
}
